import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { Input } from "@/components/ui/input";
import { FEEDBACK_TAGS } from "@/lib/docFeedbackTags";

type Row = {
  id: string;
  doc_id: string;
  collection: string;
  doc_version: string | null;
  user_id: string | null;
  kind: string;
  category: string | null;
  note: string | null;
  context: string | null;
  status: string;
  resolution_note: string | null;
  suggested_term: string | null;
  tag: string | null;
  quote: string | null;
  quote_context: string | null;
  paragraph_index: number | null;
  created_at: string;
};

const KIND_LABEL: Record<string, string> = {
  confusing: "Confusing",
  wrong_or_outdated: "Wrong / outdated",
  page_broken: "Page broken",
  missing_term: "Missing term",
};

function docPath(collection: string, docId: string) {
  if (docId === "FAQ-000") return "/support/library/dictionary";
  return collection === "faq" ? `/support/library/${docId}` : `/support/library/${docId}`;
}

export function DocFeedbackAdmin({ onCountChange }: { onCountChange?: (n: number) => void }) {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState("all");
  const [status, setStatus] = useState("new");
  const [category, setCategory] = useState("all");
  const [tagFilter, setTagFilter] = useState("all");
  const [docFilter, setDocFilter] = useState("");
  const [resolutionDraft, setResolutionDraft] = useState<Record<string, string>>({});

  async function load() {
    setLoading(true);
    const { data, error } = await supabase
      .from("doc_feedback" as any)
      .select("*")
      .order("created_at", { ascending: false })
      .limit(500);
    if (error) toast.error(error.message);
    setRows(((data as any[]) || []) as Row[]);
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  useEffect(() => {
    onCountChange?.(rows.filter((r) => r.status === "new").length);
  }, [rows, onCountChange]);

  const filtered = useMemo(
    () =>
      rows.filter(
        (r) =>
          (kind === "all" || r.kind === kind) &&
          (status === "all" || r.status === status) &&
          (category === "all" || (r.category ?? "confusion") === category) &&
          (tagFilter === "all" || (r.tag ?? "") === tagFilter) &&
          (docFilter.trim() === "" ||
            r.doc_id.toLowerCase().includes(docFilter.trim().toLowerCase())),
      ),
    [rows, kind, status, category, tagFilter, docFilter],
  );

  const grouped = useMemo(() => {
    const m = new Map<string, { doc_id: string; collection: string; count: number }>();
    for (const r of rows) {
      const key = `${r.collection}:${r.doc_id}`;
      const cur = m.get(key) || { doc_id: r.doc_id, collection: r.collection, count: 0 };
      cur.count += 1;
      m.set(key, cur);
    }
    return Array.from(m.values()).sort((a, b) => b.count - a.count).slice(0, 12);
  }, [rows]);

  const groupedTerms = useMemo(() => {
    const m = new Map<string, { display: string; count: number; docs: Set<string> }>();
    for (const r of rows) {
      if (r.kind !== "missing_term" || !r.suggested_term) continue;
      const key = r.suggested_term.trim().toLowerCase();
      if (!key) continue;
      const cur = m.get(key) || { display: r.suggested_term.trim(), count: 0, docs: new Set<string>() };
      cur.count += 1;
      cur.docs.add(r.doc_id);
      m.set(key, cur);
    }
    return Array.from(m.values()).sort((a, b) => b.count - a.count).slice(0, 20);
  }, [rows]);

  async function updateRow(id: string, patch: Partial<Row>) {
    const { error } = await supabase.from("doc_feedback" as any).update(patch).eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Updated");
    load();
  }

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <h3 className="text-sm font-semibold mb-3">Docs with the most notes</h3>
        {grouped.length === 0 ? (
          <p className="text-sm text-muted-foreground">No feedback yet.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {grouped.map((g) => (
              <Link
                key={`${g.collection}:${g.doc_id}`}
                to={docPath(g.collection, g.doc_id)}
                className="text-xs px-2.5 py-1.5 rounded border hover:bg-muted flex items-center gap-1.5"
              >
                <span className="font-mono">{g.doc_id}</span>
                <Badge variant="secondary" className="text-[10px]">{g.count}</Badge>
              </Link>
            ))}
          </div>
        )}
      </Card>

      {groupedTerms.length > 0 && (
        <Card className="p-4">
          <h3 className="text-sm font-semibold mb-3">Most-requested missing terms</h3>
          <div className="flex flex-wrap gap-2">
            {groupedTerms.map((g) => (
              <div
                key={g.display.toLowerCase()}
                className="text-xs px-2.5 py-1.5 rounded border bg-muted/30 flex items-center gap-1.5"
                title={`Reported on ${g.docs.size} doc(s)`}
              >
                <span className="font-medium">{g.display}</span>
                <Badge variant="secondary" className="text-[10px]">{g.count}</Badge>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All kinds</SelectItem>
              <SelectItem value="confusing">Confusing</SelectItem>
              <SelectItem value="wrong_or_outdated">Wrong / outdated</SelectItem>
              <SelectItem value="page_broken">Page broken</SelectItem>
              <SelectItem value="missing_term">Missing terms</SelectItem>
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="triaged">Triaged</SelectItem>
              <SelectItem value="fixed">Fixed</SelectItem>
              <SelectItem value="dismissed">Dismissed</SelectItem>
            </SelectContent>
          </Select>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="confusion">Confusion</SelectItem>
              <SelectItem value="suggestion">Suggestions</SelectItem>
            </SelectContent>
          </Select>
          <Select value={tagFilter} onValueChange={setTagFilter}>
            <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All tags</SelectItem>
              {FEEDBACK_TAGS.map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            value={docFilter}
            onChange={(e) => setDocFilter(e.target.value)}
            placeholder="Filter by doc id"
            className="w-[170px] h-9 text-sm"
          />
          <Button variant="outline" size="sm" onClick={load}>Refresh</Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">No feedback matches these filters.</p>
        ) : (
          <div className="space-y-3">
            {filtered.map((r) => (
              <div key={r.id} className="border rounded-md p-3 text-sm space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className="font-mono">{r.doc_id}</Badge>
                  <Badge variant="secondary" className="text-[10px]">{r.collection}</Badge>
                  <Badge
                    className={
                      (r.category ?? "confusion") === "suggestion"
                        ? "bg-amber-100 text-amber-900 hover:bg-amber-100"
                        : "bg-sky-100 text-sky-900 hover:bg-sky-100"
                    }
                  >
                    {(r.category ?? "confusion") === "suggestion" ? "Suggestion" : "Confusion"}
                  </Badge>
                  <Badge>{KIND_LABEL[r.kind] || r.kind}</Badge>
                  {r.tag && (
                    <Badge variant="outline" className="font-mono text-[10px]">{r.tag}</Badge>
                  )}
                  <Badge variant={r.status === "new" ? "default" : "outline"}>{r.status}</Badge>
                  {r.doc_version && <span className="text-xs text-muted-foreground">v{r.doc_version}</span>}
                  <span className="text-xs text-muted-foreground ml-auto">
                    {formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}
                  </span>
                </div>
                {r.kind === "missing_term" && r.suggested_term && (
                  <div className="rounded-md border border-primary/40 bg-primary/5 px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Suggested term</div>
                    <div className="font-semibold text-base">{r.suggested_term}</div>
                  </div>
                )}
                {r.note && <p className="whitespace-pre-wrap text-sm">{r.note}</p>}
                {r.quote && (
                  <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
                    {(() => {
                      const ctx = r.quote_context || r.quote;
                      const at = ctx.indexOf(r.quote);
                      if (at === -1) return <strong>{r.quote}</strong>;
                      return (
                        <>
                          {ctx.slice(0, at)}
                          <strong>{r.quote}</strong>
                          {ctx.slice(at + r.quote.length)}
                        </>
                      );
                    })()}
                  </div>
                )}
                {r.context && <p className="text-xs text-muted-foreground font-mono">{r.context}</p>}
                {r.resolution_note && (
                  <p className="text-xs bg-muted rounded p-2"><strong>Resolution:</strong> {r.resolution_note}</p>
                )}
                {r.status !== "fixed" && r.status !== "dismissed" && (
                  <Textarea
                    placeholder="Resolution note (optional, saved with 'Fixed')"
                    className="text-xs min-h-[50px]"
                    value={resolutionDraft[r.id] ?? ""}
                    onChange={(e) => setResolutionDraft((s) => ({ ...s, [r.id]: e.target.value }))}
                  />
                )}
                <div className="flex flex-wrap gap-2">
                  <Button asChild size="sm" variant="outline">
                    <Link to={docPath(r.collection, r.doc_id)} target="_blank">
                      <ExternalLink className="h-3.5 w-3.5 mr-1" /> Open doc
                    </Link>
                  </Button>
                  {r.status !== "triaged" && (
                    <Button size="sm" variant="outline" onClick={() => updateRow(r.id, { status: "triaged" })}>
                      Mark triaged
                    </Button>
                  )}
                  {r.status !== "fixed" && (
                    <Button size="sm" onClick={() => updateRow(r.id, {
                      status: "fixed",
                      resolution_note: resolutionDraft[r.id]?.trim() || r.resolution_note || null,
                    })}>
                      Mark fixed
                    </Button>
                  )}
                  {r.status !== "dismissed" && (
                    <Button size="sm" variant="ghost" onClick={() => updateRow(r.id, { status: "dismissed" })}>
                      Dismiss
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

export default DocFeedbackAdmin;
